import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaClipboardList, FaEye } from "react-icons/fa";
import moment from "moment";
import styles from "./AdmissionCaptureIntList.module.css";
import { CustomButton } from "../../../components";
import CustomMRTTable from "../../../components/CustomMRTTable/CustomMRTTable";
import PopupModal from "../../../components/PopupModal/PopupModal";
import AxiosInstance, { TokenService } from "../../../services/Api";

const AdmissionCaptureIntList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const staff_id = clientData?.master_id || "";
  const school_id = clientData?.school_id || "";
  const academic_year = clientData?.academic_year || "";
  const { translations } = useSelector((state) => state.language);
  const t = translations?.labels || {};

  // Get params from navigation state
  const stateData = location.state || {};
  const allVerified = stateData.allVerified || false;
  const class_id = stateData.class_id || "";
  const section_id = stateData.section_id || "";
  const config_id = stateData.config_id || "";

  // ─── State ───
  const [viewData, setViewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 });
  const [modalState, setModalState] = useState({
    open: false,
    type: "success", // "success" | "error"
    message: "",
    redirectPath: "",
  });
  const [columnFilters, setColumnFilters] = useState([]);
  // ─── Fetch view data on mount ───
  const fetchViewData = async () => {
    setLoading(true);
    try {
      const filters = {};

      columnFilters.forEach((filter) => {
        filters[filter.id] = filter.value;
      });
      const payload = {
        school_id: school_id,
        academic_year: academic_year,
        class_id: filters.class_name || "",
        total_students: filters.total_students || "",
        students_passed: filters.students_passed || "",
        students_failed: filters.students_failed || "",
        pass_percentage: filters.pass_percentage || "",
        fail_percentage: filters.fail_percentage || "",
        search: apiSearch || "",
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
      };
      const response = await AxiosInstance.post(
        "/admission/annualAcadmic/resultPublish",
        payload,
      );
      if (response?.data?.status) {
        setViewData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching publish view:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchViewData();
  }, [school_id, columnFilters, apiSearch, pagination]);

  // ─── Extract data ───
  const summary = viewData?.summary || {};
  const classList = viewData?.class_list || [];

  // ─── Build columns dynamically ───
  const columns = useMemo(
    () => [
      {
        accessorKey: "academic_year",
        header: translations.labels.academicYear || "Academic Year",
      },
      {
        accessorKey: "class_name",
        header: translations.labels.class || "Class",
        enableColumnFilter: true,
      },
      {
        accessorKey: "total_students",
        header: translations.labels.totalStudents || "Total Students",
        enableColumnFilter: true,
      },
      {
        accessorKey: "students_passed",
        header: translations.labels.studentPassed || "Students Passed",
        enableColumnFilter: true,
      },
      {
        accessorKey: "pass_percentage",
        header: translations.labels.passedPercentage || "Passed Percentage",
        enableColumnFilter: true,
      },
      {
        accessorKey: "students_failed",
        header: translations.labels.studentFailed || "Students Failed",
        enableColumnFilter: true,
      },

      {
        accessorKey: "fail_percentage",
        header: translations.labels.failedPercentage || "Failed Percentage",
        enableColumnFilter: true,
      },
      {
        accessorKey: "status",
        header: translations.labels.status || "Status",
        enableColumnFilter: true,
      },
      {
        id: "actions",
        header: translations?.labels?.actions || "Actions",
        size: 100,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <div style={{ textAlign: "center" }}>
            <button
              className={styles.viewBtn}
              onClick={() => handleView(row.original)}
              title={translations?.labels?.view || "View"}
            >
              <FaEye />
            </button>
          </div>
        ),
      },
    ],
    [viewData],
  );
  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({
      ...prev,
      open: false,
    }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };
  const handleView = (row) => {
    navigate(`/user/View-Academic-Transition`, {
      state: { id: row.id, dataRows: row },
    });
  };

  return (
    <>
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleSuccessOk}
      />
      <div className={styles.container}>
        {/* ─── Summary Cards ─── */}
        <div className={styles.summaryRow}>
          <div className={styles.summaryCard}>
            <p className={styles.summaryLabel}>
              {t.totalStudents || "Total Students"}
            </p>
            <h2 className={styles.summaryValue}>
              {summary.total_students || 0}
            </h2>
            <div className={styles.summaryGender}>
              <span className={styles.genderBoys}>
                {summary.total_gender?.boys || 0}
              </span>
              <span className={styles.genderGirls}>
                {summary.total_gender?.girls || 0}
              </span>
            </div>
          </div>

          <div className={styles.summaryCard}>
            <p className={styles.summaryLabel}>
              {t.studentsPassed || "Students Passed"}
            </p>
            <h2 className={styles.summaryValue}>
              {summary.students_passed || 0}
            </h2>
            <div className={styles.summaryGender}>
              <span className={styles.genderBoys}>
                {summary.gender_passed?.boys || 0}
              </span>
              <span className={styles.genderGirls}>
                {summary.gender_passed?.girls || 0}
              </span>
            </div>
          </div>

          <div className={styles.summaryCard}>
            <p className={styles.summaryLabel}>
              {t.passedPercentage || "Passed Percentage"}
            </p>
            <h2 className={styles.summaryValue}>
              {summary.pass_percentage ?? 0}
            </h2>
            <div className={styles.summaryGender}>
              <span className={styles.genderBoys}>
                {summary.gender_pass_percentage?.boys ?? 0}
              </span>
              <span className={styles.genderGirls}>
                {summary.gender_pass_percentage?.girls ?? "0"}
              </span>
            </div>
          </div>

          <div className={styles.summaryCard}>
            <p className={styles.summaryLabel}>
              {t.studentsFailed || "Students Failed"}
            </p>
            <h2 className={styles.summaryValue}>
              {summary.students_failed || 0}
            </h2>
            <div className={styles.summaryGender}>
              <span className={styles.genderBoys}>
                {summary.gender_failed?.boys || 0}
              </span>
              <span className={styles.genderGirls}>
                {summary.gender_failed?.girls || 0}
              </span>
            </div>
          </div>

          <div className={styles.summaryCard}>
            <p className={styles.summaryLabel}>
              {t.failedPercentage || "Failed Percentage"}
            </p>
            <h2 className={styles.summaryValue}>
              {summary.fail_percentage ?? 0}
            </h2>
            <div className={styles.summaryGender}>
              <span className={styles.genderBoys}>
                {summary.gender_fail_percentage?.boys ?? 0}
              </span>
              <span className={styles.genderGirls}>
                {summary.gender_fail_percentage?.girls ?? "0"}
              </span>
            </div>
          </div>
        </div>

        {/* ─── Table ─── */}
        <div className={styles.tableSection}>
          <CustomMRTTable
            columns={columns}
            data={classList}
            loading={loading}
            enablePagination={true}
            enableFiltering={true}
            enableSearch={true}
            enableSorting={true}
            columnFilters={columnFilters}
            onColumnFiltersChange={setColumnFilters}
            pagination={pagination}
            rowCount={classList.length}
            onPaginationChange={setPagination}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            onSearchEnter={(value) => {
              setApiSearch(value);
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
          />
        </div>
      </div>
    </>
  );
};

export default AdmissionCaptureIntList;
